import React, { ReactNode, useMemo } from 'react';

export type BasicSliderMarkConfig = React.ReactNode;
export type ExtendedSliderMarkConfig = { mark: ReactNode; tooltip: ReactNode };
export type SliderMarkConfig = BasicSliderMarkConfig | ExtendedSliderMarkConfig;
export type SliderMarkConfigObject = Record<string | number, BasicSliderMarkConfig>;

function isExtendedSliderMarkConfig(entry: SliderMarkConfig): entry is ExtendedSliderMarkConfig {
  return entry !== undefined && entry !== null && typeof entry === 'object' && Object.hasOwn(entry, 'mark');
}

const useSliderMarksWithTooltips = (
  sliderMarks: Record<string | number, SliderMarkConfig> | undefined,
  transformIn: (value: number | undefined) => number | undefined,
  min?: number,
  max?: number,
  tMin?: number,
  tMax?: number,
): [SliderMarkConfigObject, SliderMarkConfigObject] | [undefined, undefined] =>
  useMemo(() => {
    if (!sliderMarks) {
      if (tMin !== undefined && tMax !== undefined) {
        return [
          { [tMin]: min, [tMax]: max },
          { [tMin]: min, [tMax]: max },
        ];
      }
      return [undefined, undefined];
    }

    let newMarks: SliderMarkConfigObject = {};
    let newTooltips: SliderMarkConfigObject = {};

    Object.keys(sliderMarks).forEach((key) => {
      const markConfig = sliderMarks[key as string | number];

      const tKey = transformIn(Number(key));
      if (tKey === undefined) {
        return;
      }

      if (isExtendedSliderMarkConfig(markConfig)) {
        newMarks[tKey] = markConfig.mark;
        newTooltips[tKey] = markConfig.tooltip;
      } else {
        newMarks[tKey] = markConfig;
        newTooltips[tKey] = markConfig;
      }
    });

    return [newMarks, newTooltips];
  }, [sliderMarks, min, max, tMin, tMax, transformIn]);

export default useSliderMarksWithTooltips;
